import { Skeleton } from "@/components/ui/skeleton";
import type { NeedsAttention } from "../catalogue";
import { STALE_AFTER_DAYS } from "../format";

/** What a chip's number MEANS, carried in its own colour and its own border. */
type ChipTone = "awaiting" | "missing" | "older";

/**
 * The first thing on the Facts tab, and the one-click route to the only action
 * that changes what a customer sees.
 *
 * KET-3853 closed having corrected 1 published panel of 13. The failure was not
 * knowing WHICH facts were wrong, and a flat catalogue cannot answer that — so
 * triage is not a filter tucked into a toolbar, it is the headline.
 *
 * Each chip is a filter, and its pressed state lives in the URL upstream, so a
 * triaged view is a link a person can paste to a teammate.
 *
 * The three chips are deliberately NOT alike. A missing value is a hole in a
 * published panel; an old value is only old. Rendering all three as identical
 * cards inside one tinted panel — which is what shipped — flattens that into a
 * row of numbers, and the one number a person must act on today disappears into
 * the other two.
 */
export function NeedsAttentionStrip({
  counts,
  pending = false,
  scopeLabel,
  active,
  onToggle,
}: {
  /** `null` when the counts could not be established — never rendered as zero. */
  counts: NeedsAttention | null;
  /**
   * The reads behind the counts have not come back YET.
   *
   * "We could not read the full list just now" is a failure report, and on a
   * cold load it was the first thing every operator saw — the counts are null
   * on first paint because nothing has arrived, which is not the same as
   * something having gone wrong. Stating a failure that has not happened
   * teaches people to disbelieve the one that has.
   */
  pending?: boolean;
  /** What the counts are counted over, stated rather than implied. `null` when there is nothing to count over. */
  scopeLabel: string | null;
  active: string | null;
  onToggle: (status: string | null) => void;
}) {
  return (
    <section aria-labelledby="protex-needs-attention" className="flex flex-col gap-2.5">
      <h2
        id="protex-needs-attention"
        className="text-xs font-bold tracking-wider text-ks-grey-1 uppercase"
      >
        Needs attention
      </h2>
      {counts === null ? (
        // Never `0` (UX S1 error) — a zero is a claim that nothing needs
        // attention, the most dangerous thing this strip could say when it has
        // not been able to look. But "could not read" is a claim too, so the
        // wait and the failure say different things.
        pending ? (
          // Three chip-shaped placeholders, as the mock-up's loading state
          // draws them: the strip keeps its footprint, so the product card and
          // the table below it do not jump when the counts land. The words are
          // still there for a screen reader — a row of grey boxes says nothing.
          <div className="flex gap-3 overflow-x-hidden" aria-busy="true">
            <span className="sr-only">Counting…</span>
            {[0, 1, 2].map((i) => (
              <Skeleton
                key={i}
                aria-hidden
                className="h-[86px] w-[190px] shrink-0 rounded-ks-md"
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-ks-ink">
            Counts unavailable — we could not read the full list just now.
          </p>
        )
      ) : (
        <>
          {/* ONE ROW at every width, three chips across, as the mock-up draws
              it — and on a phone that is a share of the width rather than three
              content-width chips in a sideways scroller.

              The scroller is what shipped, and it hid a control. macOS draws
              overlay scrollbars, so the third chip was simply cut off 107px
              past the edge with nothing on screen saying it could be reached:
              unread and untapped, on the one viewport where a reader has least
              room to hunt (outcome-audit cycles 5 and 6, O3). A triage chip
              nobody can see is not a filter, and this strip is one of three
              entry points into the work.

              Wrapping instead was tried and rejected here: the third chip moved
              to a second row and pushed the first fact row from inside the
              844px phone fold to outside it. That is the failure the scroller
              was reaching for — the strip is the tab's headline, not its first
              page — so the fix has to keep BOTH, which is what shrinking to
              thirds does. `overflow-x-auto` stays as a floor, never as the
              mechanism: nothing overflows now, and it is only there so a chip
              somehow wider than the viewport stays reachable. */}
          <div className="flex gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:gap-3 sm:overflow-x-visible sm:pb-0">
            <Chip
              status="draft"
              tone="awaiting"
              count={counts.awaiting}
              noun="awaiting approval"
              hint="Someone drafted a correction that nobody has published yet."
              active={active}
              onToggle={onToggle}
            />
            <Chip
              status="missing"
              tone="missing"
              count={counts.missing}
              noun="missing values"
              // Said on the chip, not only in the tooltip: the sub-line is what
              // makes "2 missing" mean "two published panels have a hole".
              subLine="no number published"
              hint="No number has ever been published for these — the storefront panel has a hole."
              active={active}
              onToggle={onToggle}
            />
            <Chip
              status="older"
              tone="older"
              count={counts.older}
              noun="older than expected"
              hint={`Last checked more than ${STALE_AFTER_DAYS} days ago.`}
              active={active}
              onToggle={onToggle}
            />
          </div>
          {scopeLabel && <p className="text-xs text-ks-grey-1">{scopeLabel}</p>}
        </>
      )}
    </section>
  );
}

/**
 * Tone is the chip's ground, border and number colour together — never the
 * number alone, which a person scanning at a glance reads as noise.
 */
const TONES: Record<ChipTone, { shell: string; number: string; noun: string }> = {
  awaiting: {
    shell: "border-ks-purple-3 bg-ks-purple-4",
    number: "text-ks-purple",
    noun: "text-ks-ink",
  },
  missing: {
    shell: "border-ks-danger bg-ks-surface",
    number: "text-ks-danger",
    noun: "text-ks-ink",
  },
  older: {
    shell: "border-ks-grey-4 bg-ks-surface",
    number: "text-ks-ink",
    noun: "text-ks-grey-1",
  },
};

function Chip({
  status,
  tone,
  count,
  noun,
  subLine,
  hint,
  active,
  onToggle,
}: {
  status: string;
  tone: ChipTone;
  count: number;
  noun: string;
  subLine?: string;
  hint: string;
  active: string | null;
  onToggle: (status: string | null) => void;
}) {
  const pressed = active === status;
  // A true zero is not an alarm. "0 missing values" in a red-ruled chip read as
  // a finding on the one screen whose job is to say nothing is registered, so a
  // zero wears the quiet skin and the tone returns the moment there is a count.
  const skin = TONES[count === 0 ? "older" : tone];
  return (
    <button
      type="button"
      // Narrowing a list is reading, not editing: a read-only viewer must still
      // be able to triage, or the strip is furniture to most of the team.
      data-nonmutating
      aria-pressed={pressed}
      // Count first, noun second — "3 awaiting approval", the way it is said out
      // loud — and the whole phrase is in the accessible name, so a screen
      // reader never announces a bare number.
      aria-label={`${count} ${noun}. ${hint}`}
      title={hint}
      onClick={() => onToggle(pressed ? null : status)}
      className={[
        // An equal THIRD of the row on a phone, a fixed 190px track once there
        // is room. `basis-0` rather than content width, so all three are the
        // same size and the row can never be wider than what holds it: a 190px
        // floor made three chips 570px wide on a 390px screen — the second
        // clipped mid-word, the third off-screen entirely — and content width
        // still ran 107px past the edge. The mock-up draws three across at this
        // viewport, and a third of 390px is what that is.
        //
        // The noun wraps inside that third instead of the row wrapping outside
        // it (`break-words` so a long single word cannot push past the border),
        // and the gutter tightens to `px-3` to buy that wrap the room it needs.
        // Above `sm:` nothing changes: `basis-auto` hands the chips back to the
        // 190px track they had.
        "min-h-11 min-w-0 flex-1 basis-0 rounded-ks-md border px-3 py-3 text-left",
        "sm:min-w-[190px] sm:flex-none sm:basis-auto sm:shrink-0 sm:px-4",
        skin.shell,
        // Each chip now owns its border colour, so pressed cannot be a border
        // change — a ring is the one marker that reads the same on all three.
        pressed ? "ring-2 ring-ks-purple ring-offset-1" : "hover:border-ks-purple-3",
      ].join(" ")}
    >
      <span className={`block text-2xl leading-tight font-bold ${skin.number}`}>{count}</span>
      {/* Colour is never the only carrier — the chip says what it counts. */}
      <span className={`block text-[13px] break-words ${skin.noun}`}>{noun}</span>
      {/* The third line is what makes "2 missing" mean "two published panels
          have a hole" — worth its height wherever there is room, and the first
          thing to go on a phone, where it is the difference between three
          readable chips and two clipped ones. Nothing is lost to a screen
          reader: the chip's accessible name carries the whole hint. */}
      {subLine && <span className="hidden text-[11px] text-ks-grey-1 sm:block">{subLine}</span>}
    </button>
  );
}
